/**
 * Position and show a coach-mark (`#shakeHintTooltip`, `#healHintTooltip`)
 * next to its anchor button.
 */

import * as C from "../config/constants.js";

/** Fade duration (ms) — keep in sync with `shake-hint-fade-*` in style.css. */
export const SHAKE_HINT_FADE_MS = C.SHAKE_HINT_FADE_MS != null
  ? Number(C.SHAKE_HINT_FADE_MS)
  : 280;

const exitState = new WeakMap();

/**
 * @param {HTMLElement | null | undefined} mount
 * @param {boolean} visible
 */
export function setShakeHintVisible(mount, visible) {
  if (!mount) return;

  if (visible) {
    cancelShakeHintExit(mount);
    if (!mount.classList.contains("hidden") && !mount.classList.contains("shake-hint-tooltip--out")) {
      return;
    }
    mount.classList.remove("hidden", "shake-hint-tooltip--out");
    mount.setAttribute("aria-hidden", "false");
    return;
  }

  if (mount.classList.contains("hidden") || mount.classList.contains("shake-hint-tooltip--out")) {
    return;
  }

  mount.setAttribute("aria-hidden", "true");

  var done = false;
  var finish = function () {
    if (done) return;
    done = true;
    cancelShakeHintExit(mount);
    mount.classList.remove("shake-hint-tooltip--out");
    mount.classList.add("hidden");
  };

  var onAnimationEnd = function (event) {
    if (event.target !== mount || event.animationName !== "shake-hint-fade-out") return;
    finish();
  };

  var fallbackId = setTimeout(finish, SHAKE_HINT_FADE_MS + 32);
  if (typeof mount.addEventListener === "function") {
    mount.addEventListener("animationend", onAnimationEnd);
  }
  exitState.set(mount, { onAnimationEnd: onAnimationEnd, fallbackId: fallbackId });
  mount.classList.add("shake-hint-tooltip--out");
}

function cancelShakeHintExit(mount) {
  var pending = exitState.get(mount);
  if (!pending) return;
  clearTimeout(pending.fallbackId);
  if (typeof mount.removeEventListener === "function") {
    mount.removeEventListener("animationend", pending.onAnimationEnd);
  }
  exitState.delete(mount);
  mount.classList.remove("shake-hint-tooltip--out");
}

/**
 * @param {HTMLElement | null | undefined} mount
 * @param {HTMLElement | null | undefined} anchor
 * @param {"left" | "above" | "below"} [placement]
 */
export function positionShakeHintTooltip(mount, anchor, placement) {
  if (!mount || !anchor || typeof anchor.getBoundingClientRect !== "function") return;
  var rect = anchor.getBoundingClientRect();
  if (!(rect.width > 0 && rect.height > 0)) return;
  var gap = 10;
  var mode = placement === "above" ? "above" : placement === "below" ? "below" : "left";
  mount.dataset.placement = mode;
  if (mode === "above") {
    mount.style.left = (rect.left + rect.width / 2) + "px";
    mount.style.top = (rect.top - gap) + "px";
    return;
  }
  if (mode === "below") {
    mount.style.left = (rect.left + rect.width / 2) + "px";
    mount.style.top = (rect.bottom + gap) + "px";
    return;
  }
  mount.style.left = (rect.left - gap) + "px";
  mount.style.top = (rect.top + rect.height / 2) + "px";
}
